/**
 * Countdown timer custom element.
 *
 * Vanilla custom element on purpose: it has no dependency on the theme's
 * component runtime, so it can be dropped in any block, section or snippet.
 *
 * Markup is rendered server side by `snippets/countdown-timer.liquid`, so the
 * first paint already shows the correct values (no layout shift, no flash).
 *
 * Supported data attributes:
 * @attr {string} data-mode - "date" (fixed end date) or "evergreen" (per visitor duration)
 * @attr {string} data-end-ts - Unix timestamp in seconds (date mode, computed in Liquid)
 * @attr {string} data-end-iso - ISO 8601 date with offset (date mode, fallback)
 * @attr {string} data-duration - Duration in seconds (evergreen mode)
 * @attr {string} data-restart - "true" to restart the evergreen timer once expired
 * @attr {string} data-storage-key - localStorage key used by the evergreen mode
 * @attr {string} data-show-days - "auto" | "always" | "never"
 * @attr {string} data-on-expire - "keep" | "hide" | "message" | "hide_announcement"
 * @attr {string} data-design-mode - "true" inside the theme editor
 */

const MINUTE_IN_SECONDS = 60;
const HOUR_IN_SECONDS = 3600;
const DAY_IN_SECONDS = 86400;

/** Fallback storage when localStorage is blocked (private mode, tracking prevention). */
const memoryStorage = new Map();

/**
 * @param {string} key
 * @returns {string|null}
 */
function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memoryStorage.get(key) ?? null;
  }
}

/**
 * @param {string} key
 * @param {string|number} value
 */
function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    memoryStorage.set(key, String(value));
  }
}

/**
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return value < 10 ? `0${value}` : String(value);
}

export class CountdownTimer extends HTMLElement {
  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number|null} End time in milliseconds. */
  #endTime = null;

  /** @type {boolean} */
  #expired = false;

  connectedCallback() {
    this.#endTime = this.#resolveEndTime();
    this.#update();

    if (this.#endTime !== null) {
      this.#interval = window.setInterval(() => this.#update(), 1000);
    }

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /** Re-sync immediately when the tab becomes visible again. */
  #handleVisibilityChange = () => {
    if (!document.hidden) this.#update();
  };

  #stop() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  get designMode() {
    return this.dataset.designMode === 'true';
  }

  /**
   * @returns {number|null} End time in milliseconds, or null when not configured.
   */
  #resolveEndTime() {
    if (this.dataset.mode === 'evergreen') return this.#resolveEvergreenEndTime();

    const timestamp = parseInt(this.dataset.endTs ?? '', 10);
    if (Number.isFinite(timestamp) && timestamp > 0) return timestamp * 1000;

    const parsed = Date.parse(this.dataset.endIso ?? '');
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Evergreen mode: every visitor gets their own deadline, stored locally.
   * @returns {number|null}
   */
  #resolveEvergreenEndTime() {
    const duration = parseInt(this.dataset.duration ?? '0', 10);
    if (!Number.isFinite(duration) || duration <= 0) return null;

    const now = Date.now();

    // Always show a fresh timer while editing, so the merchant sees it running.
    if (this.designMode) return now + duration * 1000;

    const key = this.dataset.storageKey || 'countdown-timer';
    const stored = parseInt(readStorage(key) ?? '', 10);

    if (Number.isFinite(stored)) {
      if (stored > now) return stored;
      if (this.dataset.restart !== 'true') return stored;
    }

    const endTime = now + duration * 1000;
    writeStorage(key, endTime);

    return endTime;
  }

  #update() {
    if (this.#endTime === null) return;

    const remaining = Math.max(this.#endTime - Date.now(), 0);
    this.#render(remaining);

    if (remaining <= 0) {
      this.#stop();
      this.#expire();
    }
  }

  /**
   * @param {number} remainingInMs
   */
  #render(remainingInMs) {
    const total = Math.floor(remainingInMs / 1000);
    const values = {
      days: Math.floor(total / DAY_IN_SECONDS),
      hours: Math.floor((total % DAY_IN_SECONDS) / HOUR_IN_SECONDS),
      minutes: Math.floor((total % HOUR_IN_SECONDS) / MINUTE_IN_SECONDS),
      seconds: total % MINUTE_IN_SECONDS,
    };

    for (const [unit, value] of Object.entries(values)) {
      const target = this.querySelector(`[data-value="${unit}"]`);
      if (!target) continue;

      const formatted = unit === 'days' && value > 99 ? String(value) : pad(value);
      if (target.textContent !== formatted) target.textContent = formatted;
    }

    if (this.dataset.showDays === 'auto') {
      const daysUnit = this.querySelector('[data-unit="days"]');
      const daysSeparator = this.querySelector('[data-separator="days"]');
      const hideDays = values.days === 0;

      if (daysUnit instanceof HTMLElement) daysUnit.hidden = hideDays;
      if (daysSeparator instanceof HTMLElement) daysSeparator.hidden = hideDays;
    }

    this.setAttribute('aria-label', this.#accessibleLabel(values));
  }

  /**
   * @param {{days: number, hours: number, minutes: number, seconds: number}} values
   * @returns {string}
   */
  #accessibleLabel(values) {
    const parts = [];
    if (values.days > 0) parts.push(`${values.days}d`);
    parts.push(`${values.hours}h`, `${values.minutes}m`, `${values.seconds}s`);

    return parts.join(' ');
  }

  #expire() {
    if (this.#expired) return;
    this.#expired = true;
    this.dataset.expired = 'true';

    this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));

    // Keep everything visible in the theme editor so the block stays selectable.
    if (this.designMode) return;

    const clock = this.querySelector('[data-countdown-clock]');
    const message = this.querySelector('[data-countdown-expired]');

    switch (this.dataset.onExpire) {
      case 'hide':
        this.hidden = true;
        break;

      case 'message':
        if (clock instanceof HTMLElement) clock.hidden = true;
        if (message instanceof HTMLElement) message.hidden = false;
        break;

      case 'hide_announcement':
        this.#hideAnnouncement();
        break;

      default:
        // "keep": the timer stays on 00:00:00.
        break;
    }
  }

  /** Hides the announcement slide, and the whole bar when no slide is left. */
  #hideAnnouncement() {
    const slide = this.closest('.announcement-bar__slide');

    if (!(slide instanceof HTMLElement)) {
      this.hidden = true;
      return;
    }

    slide.hidden = true;
    slide.style.display = 'none';

    const slides = slide.closest('.announcement-bar__slides');
    if (!slides) return;

    const allHidden = Array.from(slides.children).every(
      (child) => child instanceof HTMLElement && child.hidden
    );
    if (!allHidden) return;

    const bar = slide.closest('.announcement-bar');
    if (bar instanceof HTMLElement) {
      bar.style.display = 'none';

      const background = bar.previousElementSibling;
      if (background instanceof HTMLElement && background.classList.contains('section-background')) {
        background.style.display = 'none';
      }
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}